import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell } from 'recharts';

// Educational Icons
const GraduationCapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
  </svg>
);

const BookIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
  </svg>
);

const UsersIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const BuildingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z"></path>
    <path d="M6 12h4v4h-4v-4"></path>
    <path d="M14 12h4v4h-4v-4"></path>
    <path d="M6 20h4v2h-4v-2"></path>
    <path d="M14 20h4v2h-4v-2"></path>
  </svg>
);

const DollarSignIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="1" x2="12" y2="23"></line>
    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
  </svg>
);

const TrendingUpIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"></polyline>
    <polyline points="17 6 23 6 23 12"></polyline>
  </svg>
);

const TrendingDownIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 18 13.5 8.5 8.5 13.5 1 6"></polyline>
    <polyline points="17 18 23 18 23 12"></polyline>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const SearchIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8"></circle>
    <path d="M21 21l-4.35-4.35"></path>
  </svg>
);

const RefreshIcon = ({ spinning }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{
      animation: spinning ? 'spin 1s linear infinite' : 'none',
    }}
  >
    <polyline points="23 4 23 10 17 10"></polyline>
    <polyline points="1 20 1 14 7 14"></polyline>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
  </svg>
);

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
  </svg>
);

const SettingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const StatCard = ({ title, value, change, changeType, icon: Icon, gradient, subtitle }) => {
  const [isHovered, setIsHovered] = useState(false);

  const cardStyles = {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: '16px',
    background: gradient,
    color: 'white',
    padding: '24px',
    boxShadow: isHovered ? '0 16px 40px rgba(0,0,0,0.2)' : '0 8px 32px rgba(0,0,0,0.12)',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
  };

  return (
    <div
      style={cardStyles}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Decorative elements */}
      <div style={{
        position: 'absolute',
        top: '-40px',
        right: '-40px',
        width: '128px',
        height: '128px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.1)',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '-30px',
        left: '-30px',
        width: '96px',
        height: '96px',
        borderRadius: '50%',
        backgroundColor: 'rgba(255,255,255,0.05)',
      }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div style={{
            padding: '12px',
            borderRadius: '12px',
            backgroundColor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)'
          }}>
            <Icon />
          </div>

          {/* Placeholder for future quick actions */}
          <div style={{
            padding: '8px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255,255,255,0.1)',
            opacity: 0.7,
            fontSize: '12px',
            cursor: 'pointer',
            transition: 'opacity 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            <PlusIcon />
          </div>
        </div>

        <div style={{ fontSize: '14px', opacity: 0.8, marginBottom: '8px' }}>
          {title}
        </div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', marginBottom: '8px' }}>
          {value}
        </div>

        {subtitle && (
          <div style={{ fontSize: '12px', opacity: 0.7, marginBottom: '12px' }}>
            {subtitle}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ color: changeType === 'positive' ? '#4ADE80' : '#F87171', marginRight: '4px' }}>
            {changeType === 'positive' ? <TrendingUpIcon /> : <TrendingDownIcon />}
          </div>
          <span style={{
            fontSize: '14px',
            fontWeight: '500',
            color: changeType === 'positive' ? '#4ADE80' : '#F87171'
          }}>
            {change}% from last semester
          </span>
        </div>
      </div>
    </div>
  );
};

const ActivityTable = ({ activities }) => {
  const [filter, setFilter] = useState('all');

  const filteredActivities = Array.isArray(activities) ? activities.filter(activity =>
    filter === 'all' || activity.status === filter
  ) : [];

  const getStatusStyle = (status) => {
    const baseStyle = {
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      textTransform: 'capitalize',
    };

    switch (status) {
      case 'completed':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      case 'pending':
        return { ...baseStyle, backgroundColor: '#FEF3C7', color: '#92400E' };
      case 'processing':
        return { ...baseStyle, backgroundColor: '#DBEAFE', color: '#1E40AF' };
      case 'verified':
        return { ...baseStyle, backgroundColor: '#D1FAE5', color: '#065F46' };
      default:
        return { ...baseStyle, backgroundColor: '#F3F4F6', color: '#374151' };
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              padding: '8px 12px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="all">All Activities</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="processing">Processing</option>
            <option value="verified">Verified</option>
          </select>

          {/* Placeholder for future filters */}
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#F3F4F6',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6B7280',
            border: '1px dashed #D1D5DB',
            cursor: 'not-allowed'
          }}>
            + Date Range
          </div>
        </div>

        {/* Placeholder for future export functionality */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <button style={{
            padding: '8px 16px',
            backgroundColor: '#F3F4F6',
            color: '#6B7280',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'not-allowed',
            opacity: 0.6,
          }}>
            Export CSV
          </button>
          <button style={{
            padding: '8px 16px',
            backgroundColor: '#8B5CF6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
          >
            View All
          </button>
        </div>
      </div>

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      }}>
        {filteredActivities.map((activity, index) => (
          <div
            key={activity.id}
            style={{
              padding: '16px 20px',
              borderBottom: index < filteredActivities.length - 1 ? '1px solid #F3F4F6' : 'none',
              transition: 'background-color 0.2s',
              cursor: 'pointer',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', color: '#111827' }}>{activity.activity}</span>
                  <span style={getStatusStyle(activity.status)}>{activity.status}</span>
                </div>
                <div style={{ fontSize: '14px', color: '#6B7280' }}>
                  {activity.student && `Student: ${activity.student}`}
                  {activity.lecturer && `Lecturer: ${activity.lecturer}`}
                  {activity.course && ` • Course: ${activity.course}`}
                  {activity.unit && ` • Unit: ${activity.unit}`}
                  {activity.amount && ` • Amount: ${activity.amount}`}
                  {activity.department && ` • Department: ${activity.department}`}
                </div>
              </div>
              <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'right' }}>
                <div>{activity.date}</div>
                <div style={{ fontSize: '10px', marginTop: '2px' }}>{activity.id}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const CampusOverview = ({ campuses }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
    {campuses.map((campus, index) => (
      <div
        key={index}
        style={{
          padding: '20px',
          backgroundColor: '#F9FAFB',
          borderRadius: '12px',
          transition: 'all 0.2s',
          cursor: 'pointer',
          border: '1px solid #F3F4F6',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#F3F4F6';
          e.currentTarget.style.borderColor = '#E5E7EB';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#F9FAFB';
          e.currentTarget.style.borderColor = '#F3F4F6';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
            {campus.campus}
          </h4>
          <span style={{
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '10px',
            fontWeight: '500',
            textTransform: 'uppercase',
            backgroundColor: campus.status === 'active' ? '#D1FAE5' : campus.status === 'expanding' ? '#FEF3C7' : '#DBEAFE',
            color: campus.status === 'active' ? '#065F46' : campus.status === 'expanding' ? '#92400E' : '#1E40AF',
          }}>
            {campus.status}
          </span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '12px' }}>
          <div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Students</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{campus.students}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Capacity</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{campus.capacity}</div>
          </div>
          <div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>Occupancy</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>{campus.occupancy}%</div>
          </div>
        </div>

        <div style={{ backgroundColor: '#E5E7EB', borderRadius: '4px', height: '4px', marginBottom: '8px' }}>
          <div style={{
            backgroundColor: campus.occupancy > 80 ? '#EF4444' : campus.occupancy > 60 ? '#F59E0B' : '#10B981',
            height: '100%',
            borderRadius: '4px',
            width: `${campus.occupancy}%`,
            transition: 'width 0.3s ease',
          }} />
        </div>

        {/* Placeholder for future campus management actions */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
          <button style={{
            padding: '4px 8px',
            backgroundColor: 'transparent',
            border: '1px dashed #D1D5DB',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#6B7280',
            cursor: 'not-allowed',
          }}>
            + Manage
          </button>
          <button style={{
            padding: '4px 8px',
            backgroundColor: 'transparent',
            border: '1px dashed #D1D5DB',
            borderRadius: '6px',
            fontSize: '11px',
            color: '#6B7280',
            cursor: 'not-allowed',
          }}>
            + Reports
          </button>
        </div>
      </div>
    ))}
  </div>
);

const Dashboard = ({ institutionName }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  // Data states
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalCourses: 0,
    totalLecturers: 0,
    totalCampuses: 0,
  });
  const [enrollmentData, setEnrollmentData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [campusOverview, setCampusOverview] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // API base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      };

      // Fetch all data concurrently
      const [
        studentsResponse,
        coursesResponse,
        lecturersResponse,
        campusesResponse,
        paymentsResponse,
        attendanceResponse
      ] = await Promise.allSettled([
        fetch(`${API_BASE_URL}/students`, { headers }),
        fetch(`${API_BASE_URL}/courses`, { headers }),
        fetch(`${API_BASE_URL}/lecturers`, { headers }),
        fetch(`${API_BASE_URL}/campuses`, { headers }),
        fetch(`${API_BASE_URL}/payments`, { headers }),
        fetch(`${API_BASE_URL}/attendance`, { headers })
      ]);

      // Process students data
      let students = [];
      if (studentsResponse.status === 'fulfilled' && studentsResponse.value.ok) {
        students = await studentsResponse.value.json();
      }

      // Process courses data
      let courses = [];
      if (coursesResponse.status === 'fulfilled' && coursesResponse.value.ok) {
        courses = await coursesResponse.value.json();
      }

      // Process lecturers data
      let lecturers = [];
      if (lecturersResponse.status === 'fulfilled' && lecturersResponse.value.ok) {
        lecturers = await lecturersResponse.value.json();
      }

      // Process campuses data
      let campuses = [];
      if (campusesResponse.status === 'fulfilled' && campusesResponse.value.ok) {
        campuses = await campusesResponse.value.json();
      }

      // Process payments data
      let payments = [];
      if (paymentsResponse.status === 'fulfilled' && paymentsResponse.value.ok) {
        payments = await paymentsResponse.value.json();
      }

      // Process attendance data
      let attendance = [];
      if (attendanceResponse.status === 'fulfilled' && attendanceResponse.value.ok) {
        const attendanceData = await attendanceResponse.value.json();
        attendance = attendanceData.records || attendanceData || [];
      }

      // Update stats
      setStats({
        totalStudents: students.length,
        totalCourses: courses.length,
        totalLecturers: lecturers.length,
        totalCampuses: campuses.length,
      });

      // Generate enrollment analytics based on actual data
      const studentsCount = Array.isArray(students) ? students.length : 0;
      const lecturersCount = Array.isArray(lecturers) ? lecturers.length : 0;
      const coursesCount = Array.isArray(courses) ? courses.length : 0;

      // Create realistic growth patterns based on current data - last 6 months
      const currentDate = new Date();
      const months = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
        months.push(date.toLocaleString('default', { month: 'short' }));
      }

      const enrollmentAnalytics = months.map((month, index) => {
        const progress = (index + 1) / months.length; // Gradual growth over time
        return {
          month,
          students: Math.floor(studentsCount * progress),
          lecturers: Math.floor(lecturersCount * progress),
          courses: Math.floor(coursesCount * progress)
        };
      });

      setEnrollmentData(enrollmentAnalytics);

      // Generate department distribution based on courses
      const courseCategories = Array.isArray(courses) ? courses.reduce((acc, course) => {
        // Simple categorization based on course name keywords
        let category = 'General Studies';
        if (course.name.toLowerCase().includes('theology') || course.name.toLowerCase().includes('biblical')) {
          category = 'Theology';
        } else if (course.name.toLowerCase().includes('pastoral') || course.name.toLowerCase().includes('ministry')) {
          category = 'Pastoral Care';
        } else if (course.name.toLowerCase().includes('leadership') || course.name.toLowerCase().includes('church')) {
          category = 'Church Leadership';
        }

        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {}) : {};

      const totalCoursesCount = Array.isArray(courses) ? courses.length : 0;
      const departmentColors = ['#8B5CF6', '#06B6D4', '#F59E0B', '#EF4444', '#10B981'];
      const departmentChartData = Object.entries(courseCategories).map(([name, count], index) => ({
        name,
        value: Math.round((count / totalCoursesCount) * 100),
        color: departmentColors[index % departmentColors.length],
        students: Math.floor((Array.isArray(students) ? students.length : 0) * (count / totalCoursesCount))
      }));
      setDepartmentData(departmentChartData);

      // Generate recent activities from payments and attendance
      const activities = [];

      // Add payment activities
      if (Array.isArray(payments)) {
        payments.slice(0, 3).forEach((payment, index) => {
          activities.push({
            id: `#PAY-${payment.id}`,
            activity: 'Fee Payment Received',
            student: payment.Student?.name || payment.student?.name || 'Unknown Student',
            amount: `KSH ${payment.amount?.toLocaleString() || '0'}`,
            status: payment.status === 'confirmed' ? 'verified' : 'pending',
            date: new Date(payment.createdAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })
          });
        });
      }

      // Add attendance activities (if available)
      if (Array.isArray(attendance)) {
        attendance.slice(0, 2).forEach((record, index) => {
          activities.push({
            id: `#ATT-${record.id}`,
            activity: 'Attendance Recorded',
            student: record.Student?.name || record.student?.name || 'Unknown Student',
            course: record.Unit?.Course?.name || 'Unknown Course',
            status: record.present ? 'completed' : 'pending',
            date: new Date(record.createdAt).toLocaleString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit'
            })
          });
        });
      }

      // Add some default activities if no real data
      if (activities.length === 0) {
        activities.push(
          {
            id: '#ACT-001',
            activity: 'New Student Registration',
            student: students[0]?.name || 'John Mwangi',
            course: courses[0]?.name || 'Theology Diploma',
            status: 'completed',
            date: '2 min ago'
          },
          {
            id: '#ACT-002',
            activity: 'Lecturer Assignment',
            lecturer: lecturers[0]?.name || 'Dr. Grace Wanjiku',
            course: courses[0]?.name || 'Systematic Theology',
            status: 'pending',
            date: '5 min ago'
          }
        );
      }

      setRecentActivities(activities.slice(0, 5));

      // Generate campus overview
      const campusData = await Promise.all(campuses.map(async (campus) => {
        // Count students per campus
        const campusStudents = students.filter(student => student.campusId === campus.id).length;
        const capacity = campus.capacity || 100; // Default capacity
        const occupancy = capacity > 0 ? Math.round((campusStudents / capacity) * 100) : 0;

        return {
          campus: campus.name,
          students: campusStudents,
          capacity: capacity,
          occupancy: occupancy,
          status: occupancy > 80 ? 'active' : occupancy > 60 ? 'expanding' : 'new'
        };
      }));
      setCampusOverview(campusData);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load dashboard data. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchDashboardData();
    setTimeout(() => setRefreshing(false), 500);
  };

  const handleGenerateReport = () => {
    // Generate a comprehensive academic report
    const reportData = {
      generatedAt: new Date().toISOString(),
      stats: stats,
      departmentData: departmentData,
      campusOverview: campusOverview,
      recentActivities: recentActivities
    };

    // Create a downloadable JSON report
    const dataStr = JSON.stringify(reportData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `npbc-academic-report-${new Date().toISOString().split('T')[0]}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();

    // Show success notification (you could implement a toast system here)
    alert('Academic report generated and downloaded successfully!');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 50%, #E2E8F0 100%)',
    }}>
      {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        backgroundColor: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid #E5E7EB',
        padding: '16px 24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #8B5CF6 30%, #06B6D4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
            }}>
              {institutionName ? `${institutionName} Portal` : 'Institution Portal'}
            </h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '16px' }}>
              {institutionName || 'Educational Institution'} • Academic Year 2024/2025
            </p>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: '#6B7280',
              fontSize: '14px'
            }}>
              <CalendarIcon />
              <span>{currentTime.toLocaleDateString()}</span>
              <span style={{ fontFamily: 'monospace' }}>{currentTime.toLocaleTimeString()}</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9CA3AF',
                  pointerEvents: 'none',
                }}>
                  <SearchIcon />
                </div>
                <input
                  type="text"
                  placeholder="Search students, courses..."
                  style={{
                    paddingLeft: '40px',
                    paddingRight: '16px',
                    paddingTop: '8px',
                    paddingBottom: '8px',
                    backgroundColor: '#F3F4F6',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    outline: 'none',
                    transition: 'all 0.2s',
                    width: '250px',
                  }}
                  onFocus={(e) => {
                    e.target.style.backgroundColor = 'white';
                    e.target.style.boxShadow = '0 0 0 2px #8B5CF6';
                  }}
                  onBlur={(e) => {
                    e.target.style.backgroundColor = '#F3F4F6';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              <button
                onClick={handleRefresh}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              >
                <RefreshIcon spinning={refreshing} />
              </button>

              <button
                style={{
                  position: 'relative',
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              >
                <BellIcon />
                <span style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#EF4444',
                  borderRadius: '50%',
                }} />
              </button>

              <button
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
              >
                <SettingsIcon />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div style={{ padding: '24px' }}>
        {/* Loading State */}
        {loading && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px',
            backgroundColor: 'white',
            borderRadius: '16px',
            marginBottom: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #F3F4F6',
              borderTop: '4px solid #8B5CF6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '16px',
            }} />
            <span style={{ fontSize: '16px', color: '#6B7280' }}>Loading dashboard data...</span>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div style={{
            padding: '20px',
            backgroundColor: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '12px',
            marginBottom: '24px',
            color: '#DC2626',
          }}>
            <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>
              Error Loading Dashboard
            </div>
            <div style={{ fontSize: '14px', marginBottom: '16px' }}>
              {error}
            </div>
            <button
              onClick={handleRefresh}
              style={{
                padding: '8px 16px',
                backgroundColor: '#DC2626',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer',
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {!loading && !error && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
          }}>
            <StatCard
              title="Total Students"
              value={stats.totalStudents.toString()}
              subtitle="Active enrollments"
              change={12.5}
              changeType="positive"
              icon={GraduationCapIcon}
              gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
            />
            <StatCard
              title="Academic Programs"
              value={stats.totalCourses.toString()}
              subtitle="Active courses"
              change={8.2}
              changeType="positive"
              icon={BookIcon}
              gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
            />
            <StatCard
              title="Faculty Members"
              value={stats.totalLecturers.toString()}
              subtitle="Teaching staff"
              change={15.4}
              changeType="positive"
              icon={UsersIcon}
              gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
            />
            <StatCard
              title="Campus Locations"
              value={stats.totalCampuses.toString()}
              subtitle="Active centers"
              change={25.0}
              changeType="positive"
              icon={BuildingIcon}
              gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
            />
          </div>
        )}

        {/* Quick Actions */}
        {!loading && !error && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '600', color: '#374151' }}>
              Quick Actions
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <button
                onClick={() => navigate('/students')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#F0F9FF',
                  border: '1px solid #0EA5E9',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#0C4A6E',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#E0F2FE';
                  e.target.style.borderColor = '#0284C7';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#F0F9FF';
                  e.target.style.borderColor = '#0EA5E9';
                }}
              >
                <UsersIcon />
                Student Management
              </button>

              <button
                onClick={() => navigate('/courses')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#FEF7CD',
                  border: '1px solid #EAB308',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#713F12',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#FEF3C7';
                  e.target.style.borderColor = '#CA8A04';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#FEF7CD';
                  e.target.style.borderColor = '#EAB308';
                }}
              >
                <BookIcon />
                Course Management
              </button>

              <button
                onClick={() => navigate('/lecturers')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#F3E8FF',
                  border: '1px solid #A855F7',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#581C87',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#EDE9FE';
                  e.target.style.borderColor = '#9333EA';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#F3E8FF';
                  e.target.style.borderColor = '#A855F7';
                }}
              >
                <UsersIcon />
                Lecturer Management
              </button>

              <button
                onClick={() => navigate('/payments')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#F0FDF4',
                  border: '1px solid #22C55E',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#14532D',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#DCFCE7';
                  e.target.style.borderColor = '#16A34A';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#F0FDF4';
                  e.target.style.borderColor = '#22C55E';
                }}
              >
                <DollarSignIcon />
                Fee Collection
              </button>

              <button
                onClick={() => {
                  // Generate and download reports
                  handleGenerateReport();
                }}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#FEF3F2',
                  border: '1px solid #F97316',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#9A3412',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#FFEDD5';
                  e.target.style.borderColor = '#EA580C';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#FEF3F2';
                  e.target.style.borderColor = '#F97316';
                }}
              >
                📊
                Academic Reports
              </button>

              <button
                onClick={() => navigate('/campuses')}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#F0F9FF',
                  border: '1px solid #06B6D4',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#164E63',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#CFFAFE';
                  e.target.style.borderColor = '#0891B2';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#F0F9FF';
                  e.target.style.borderColor = '#06B6D4';
                }}
              >
                <BuildingIcon />
                Campus Management
              </button>
            </div>
          </div>
        )}

        {/* Main Charts */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px',
          marginBottom: '24px',
        }}>
          {/* Enrollment Analytics */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease',
            height: '400px',
          }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  Enrollment Analytics
                </h3>
                <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
                  Student and lecturer growth over time
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}>Students</button>
                <button style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  backgroundColor: 'transparent',
                  color: '#6B7280',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}>Lecturers</button>
              </div>
            </div>
            <div style={{ height: '280px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={enrollmentData}>
                  <defs>
                    <linearGradient id="colorStudents" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorLecturers" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#06B6D4" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <XAxis dataKey="month" stroke="#6B7280" />
                  <YAxis stroke="#6B7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1F2937',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
                      color: 'white'
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="students"
                    stroke="#8B5CF6"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorStudents)"
                  />
                  <Area
                    type="monotone"
                    dataKey="lecturers"
                    stroke="#06B6D4"
                    strokeWidth={3}
                    fillOpacity={1}
                    fill="url(#colorLecturers)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Department Distribution */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease',
            height: '400px',
          }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'}
          >
            <h3 style={{ margin: '0 0 24px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
              Department Distribution
            </h3>
            <div style={{ height: '200px', marginBottom: '16px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={departmentData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {departmentData.map((item, index) => (
                <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '12px',
                      height: '12px',
                      borderRadius: '50%',
                      backgroundColor: item.color,
                    }} />
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>{item.name}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>{item.students}</div>
                    <div style={{ fontSize: '12px', color: '#6B7280' }}>{item.value}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: '24px',
        }}>
          {/* Recent Activities */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease',
          }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  Recent Activities
                </h3>
                <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
                  Latest academic and administrative activities
                </p>
              </div>
            </div>
            <ActivityTable activities={recentActivities} />
          </div>

          {/* Campus Overview */}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
            transition: 'box-shadow 0.3s ease',
          }}
            onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.12)'}
            onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  Campus Overview
                </h3>
                <p style={{ margin: 0, color: '#6B7280', fontSize: '14px' }}>
                  Multi-campus status
                </p>
              </div>
            </div>
            <CampusOverview campuses={campusOverview} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 1024px) {
          .grid-2fr-1fr {
            grid-template-columns: 1fr !important;
          }
        }
        
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
